import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, Gift, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';

const TrialBanner = ({ trialStatus, userRole, pilotStatus }) => {
  const navigate = useNavigate();

  if (!trialStatus) return null;

  // Don't show trial/upgrade banners for active PILOT users
  if (pilotStatus === 'active') return null;

  const { is_trial, trial_ended, days_remaining, trial_ends_at } = trialStatus;

  // Don't show banner if not in trial
  if (!is_trial) return null;

  // Trial ended - show upgrade banner
  if (trial_ended) {
    return (
      <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded-lg">
        <div className="flex items-start">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="ml-3 flex-1">
            <h3 className="text-base font-semibold text-red-800">
              Your free trial has ended
            </h3>
            <p className="text-red-700 mt-1 text-sm">
              You can still create projects and resources in draft mode, but you cannot publish them without an active plan.
            </p>
            <Button
              className="mt-3 bg-red-600 hover:bg-red-700 text-white"
              onClick={() => navigate('/pricing')}
            >
              Choose a Plan
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Trial active - show countdown
  const getDaysText = (days) => {
    if (days === 0) return "today";
    if (days === 1) return "tomorrow";
    return `in ${days} days`;
  };

  const getBannerColor = () => {
    if (days_remaining <= 1) return 'bg-red-50 border-red-500';
    if (days_remaining <= 3) return 'bg-orange-50 border-orange-500';
    if (days_remaining <= 7) return 'bg-yellow-50 border-yellow-500';
    return 'bg-green-50 border-green-500';
  };

  const getTextColor = () => {
    if (days_remaining <= 1) return 'text-red-800';
    if (days_remaining <= 3) return 'text-orange-800';
    if (days_remaining <= 7) return 'text-yellow-800';
    return 'text-green-800';
  };

  const getSubTextColor = () => {
    if (days_remaining <= 1) return 'text-red-700';
    if (days_remaining <= 3) return 'text-orange-700';
    if (days_remaining <= 7) return 'text-yellow-700';
    return 'text-green-700';
  };

  const getIconColor = () => {
    if (days_remaining <= 1) return 'text-red-600';
    if (days_remaining <= 3) return 'text-orange-600';
    if (days_remaining <= 7) return 'text-yellow-600';
    return 'text-green-600';
  };

  return (
    <div className={`${getBannerColor()} border-l-4 p-4 mb-6 rounded-lg`}>
      <div className="flex items-start">
        <Gift className={`w-5 h-5 ${getIconColor()} flex-shrink-0 mt-0.5`} />
        <div className="ml-3 flex-1">
          <h3 className={`text-base font-semibold ${getTextColor()}`}>
            🎁 Free Starter Trial - {days_remaining} day{days_remaining > 1 ? 's' : ''} remaining
          </h3>
          <p className={`${getSubTextColor()} mt-1 text-sm`}>
            Your trial period ends {getDaysText(days_remaining)}. Enjoy all features for free!
          </p>
          {days_remaining <= 7 && (
            <Button
              variant="outline"
              size="sm"
              className="mt-3"
              onClick={() => navigate('/pricing')}
            >
              View Plans
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TrialBanner;
